<template>
  <SoDialog
    v-model="isOpen"
    max-width="503"
    :card-title="`${fuelLabel} Meter Frequency`"
    @on-close="cancel()"
  >
    <SoForm data-testid="change-read-freq-form" @submit="handleSubmit">
      <SoAlert
        v-if="hasTou && showTouAlert"
        type="warning"
        title="Half-Hourly required for Time Of Use tariff"
        data-testid="tou-warning-alert"
        :show-close-button="true"
        @close="toggleTouAlert"
      >
        <p>
          Electricity readings are required every 30 mins for the customer’s
          Time Of Use tariff. Whilst the tariff is active frequencies can be
          changed for the gas meter(s) only.
        </p>
      </SoAlert>

      <SoAlert
        v-if="hasExportOnly && showExportOnlyAlert"
        type="warning"
        data-testid="export-only-waring-alert"
        :show-close-button="true"
        @close="toggleExportOnlyAlert"
      >
        <p>
          Electricity export meters will send readings on a monthly basis in
          line with the energy bill. We're currently unable to receive readings
          form an export meter more frequently.
        </p>
      </SoAlert>

      <SoAlert
        v-if="hasExport && showExportAlert"
        type="warning"
        data-testid="export-waring-alert"
        :show-close-button="true"
        @close="toggleExportAlert"
      >
        <p>
          These settings only apply to import electricity and gas meters, not to
          export electricity meter
        </p>
      </SoAlert>

      <p v-else-if="!hasTou" data-testid="read-frequency-subtitle">
        Changes will only be applied to the
        {{ fuelLabel.toLowerCase() }} smart meters for this account.
      </p>

      <div
        v-if="!isGas"
        class="pt-6"
        data-testid="settlement-frequency-section"
      >
        <div
          v-if="!showSettlementAlert"
          class="d-flex flex-row justify-space-between align-center"
        >
          <h5>Data used for settlement</h5>
          <SoIcon
            size="small"
            icon="help-circle"
            class="d-flex align-center"
            data-testid="settlement-info-toggle-icon"
            @click="toggleSettlementAlert"
          ></SoIcon>
        </div>
        <SoAlert
          v-if="showSettlementAlert"
          type="info"
          title="Data used for settlement"
          data-testid="settlement-alert-info"
          :show-close-button="true"
          @close="toggleSettlementAlert"
        >
          <div class="mt-3">
            <p>
              We’ll use this data to accurately determine how much electricity
              to buy in advance from energy generators, which means better
              prices and rewards for the customer.
            </p>
            <br />
            <p>
              The below frequency will be applied to all of the
              {{ fuelLabel.toLowerCase() }} smart meters for this account.
            </p>
          </div>
        </SoAlert>

        <SoRadioGroup
          v-model="settlementFrequency"
          :items="settlementFrequencyOptions"
          :disabled="radioButtonsDisabled"
          :rules="radioButtonsDisabled ? [] : [rules.required]"
          data-testid="settlement-frequency-options"
        />
      </div>

      <div :class="{ 'pt-6': isGas }">
        <div
          v-if="!showReadingFreqAlert"
          class="d-flex flex-row justify-space-between align-center"
        >
          <h5>Data from your meter</h5>
          <SoIcon
            size="small"
            icon="help-circle"
            class="d-flex align-center"
            data-testid="read-freq-info-toggle-icon"
            @click="toggleReadFreqAlert"
          ></SoIcon>
        </div>

        <SoAlert
          v-if="showReadingFreqAlert"
          type="info"
          title="Data from the meter"
          data-testid="read-freq-alert-info"
          :show-close-button="true"
          @close="toggleReadFreqAlert"
        >
          <div class="mt-3">
            <p>With half-hourly readings, we can:</p>
            <br />
            <ul class="ml-6">
              <li>Keep bills accurate based on actual reads</li>
              <li>Quickly spot and fix any meter issues</li>
              <li>
                Share personalised energy insights with the customer to help
                reduce their energy costs
              </li>
            </ul>
            <br />
            <p>
              The below frequency will be applied to all of the
              {{ fuelLabel.toLowerCase() }} smart meters for this account.
            </p>
          </div>
        </SoAlert>

        <SoRadioGroup
          v-model="meterFrequency"
          :items="meterFrequencyOptions"
          :disabled="radioButtonsDisabled"
          :rules="radioButtonsDisabled ? [] : [rules.required]"
          data-testid="meter-frequency-options"
        />
      </div>
      <SoAlert
        v-if="showChangesAlert"
        class="mb-3"
        type="info"
        text="No changes to save. Your settings remain unchanged."
        data-testid="no-changes-alert"
      >
      </SoAlert>
      <div class="button-row">
        <SoButton
          id="cancel-button"
          class="mr-2"
          large
          title="Cancel"
          type="secondary"
          data-testid="cancel-read-frequency-button"
          @click="cancel()"
        />
        <SoButton
          id="update-button"
          title="Update"
          type="primary"
          submit
          large
          :loading="updateMeterpointFreqConsent.isPending"
          data-testid="update-read-frequency-button"
        />
      </div>
    </SoForm>
  </SoDialog>
</template>
<script lang="ts">
  import SoDialog from "@/components/SoDialog.vue"
  import { defineComponent, PropType } from "vue"
  import SoRadioGroup from "@/components/formControls/SoRadioGroup.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import { SmartReadFrequencies } from "@/models/consent/consent.types"
  import { MeterReadingsMutator } from "@/mutators/meterReadings/MeterReadingsMutator"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { useSnackbarStore } from "@/stores/snackbar"
  import rules from "@/validators/rules"
  import {
    ConsentDefinition,
    UpdateConsentRequest,
  } from "@/api/backofficeRest/generated/model"
  import SoForm from "@/components/formControls/SoForm.vue"
  import { errorReporter } from "@/services/ErrorService"
  import { ConsentSettingType } from "@/models/meterpointConsent/meterpointConsent.types"
  import { SoRadioGroupItem } from "@/types/components/SoRadioGroup"

  const TOU_HH_REQUIRED_MESSAGE =
    "Half-Hourly is required on the Time Of Use tariff"

  export default defineComponent({
    components: {
      SoForm,
      SoAlert,
      SoIcon,
      SoDialog,
      SoRadioGroup,
      SoButton,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: true,
        required: true,
      },
      hasTou: {
        type: Boolean,
      },
      hasExport: {
        type: Boolean,
      },
      hasExportOnly: {
        type: Boolean,
      },
      isGas: {
        type: Boolean,
      },
      currentReadFrequency: {
        type: String as PropType<SmartReadFrequencies | null>,
        default: null,
      },
      currentSettlementFrequency: {
        type: String as PropType<string | null>,
        default: null,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        current: useCurrentAccountStore(),
        snackbar: useSnackbarStore(),
        isChangeReadFrequencyModalOpen: true,
        settlementFrequency: "",
        meterFrequency: "",
        frequencyOptions: [
          {
            text: "Half-Hourly",
            value: SmartReadFrequencies.HALF_HOURLY,
            status: { type: "normal", text: "Recommended" },
          },
          {
            text: "Daily",
            value: SmartReadFrequencies.DAILY,
          },
          {
            text: "Monthly",
            value: SmartReadFrequencies.MONTHLY,
          },
        ],
        showSettlementAlert: false,
        showReadingFreqAlert: false,
        showTouAlert: true,
        showExportAlert: true,
        showExportOnlyAlert: true,
        showChangesAlert: false,
        rules: rules,
        updateMeterpointFreqConsent:
          MeterReadingsMutator.updateMeterpointReadFreqConsent(),
      }
    },
    computed: {
      isOpen: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      fuelLabel(): string {
        return this.isGas ? "Gas" : "Electricity"
      },
      radioButtonsDisabled() {
        return this.hasExportOnly
      },
      settlementFrequencyOptions(): SoRadioGroupItem[] {
        if (!this.hasTou) return this.frequencyOptions
        return this.frequencyOptions.map((option) =>
          option.value === SmartReadFrequencies.HALF_HOURLY
            ? option
            : {
                ...option,
                disabled: true,
                disabledRadioMessage: TOU_HH_REQUIRED_MESSAGE,
              },
        )
      },
      meterFrequencyOptions(): SoRadioGroupItem[] {
        const disableOption = (option: SoRadioGroupItem, message?: string) => ({
          ...option,
          disabled: true,
          ...(message && { disabledRadioMessage: message }),
        })

        const constraints = {
          [SmartReadFrequencies.DAILY]: {
            [SmartReadFrequencies.HALF_HOURLY]:
              "Requires half-hourly settlement frequency",
          },
          [SmartReadFrequencies.MONTHLY]: {
            [SmartReadFrequencies.HALF_HOURLY]:
              "Requires half-hourly settlement frequency",
            [SmartReadFrequencies.DAILY]: "Requires daily settlement frequency",
          },
        }

        return this.frequencyOptions.map((option) => {
          if (
            this.hasTou &&
            option.value !== SmartReadFrequencies.HALF_HOURLY
          ) {
            return disableOption(option, TOU_HH_REQUIRED_MESSAGE)
          }

          const message = this.isGas
            ? undefined
            : constraints[this.settlementFrequency]?.[option.value]
          if (message) {
            return disableOption(option, message)
          }

          return option
        })
      },
    },
    watch: {
      isOpen(newVal) {
        if (newVal) {
          this.settlementFrequency = this.currentSettlementFrequency || ""
          this.meterFrequency = this.currentReadFrequency || ""
        }
      },
      meterFrequency() {
        this.showChangesAlert = false
      },
      settlementFrequency(newValue) {
        this.showChangesAlert = false
        if (
          newValue === SmartReadFrequencies.DAILY &&
          this.meterFrequency === SmartReadFrequencies.HALF_HOURLY
        ) {
          this.meterFrequency = ""
        }
        if (
          newValue === SmartReadFrequencies.MONTHLY &&
          (this.meterFrequency === SmartReadFrequencies.HALF_HOURLY ||
            this.meterFrequency === SmartReadFrequencies.DAILY)
        ) {
          this.meterFrequency = ""
        }
      },
    },
    mounted() {
      if (this.currentSettlementFrequency) {
        this.settlementFrequency = this.currentSettlementFrequency
      }

      if (this.currentReadFrequency) {
        this.meterFrequency = this.currentReadFrequency
      }
    },
    methods: {
      async handleSubmit() {
        if (
          this.currentReadFrequency === this.meterFrequency &&
          (this.isGas ||
            this.currentSettlementFrequency === this.settlementFrequency)
        ) {
          this.showChangesAlert = true
          return
        }
        const payload: UpdateConsentRequest[] = this.isGas
          ? [
              {
                consentDefinition: ConsentDefinition.MPRN_BILLING,
                setting: this.meterFrequency as ConsentSettingType,
              },
            ]
          : [
              {
                consentDefinition: ConsentDefinition.MPAN_BILLING,
                setting: this.meterFrequency as ConsentSettingType,
              },
              {
                consentDefinition: ConsentDefinition.MPAN_SETTLEMENT,
                setting: this.settlementFrequency as ConsentSettingType,
              },
            ]
        try {
          const accountId = this.current.coreId.data
          if (!accountId) throw new Error("Missing coreId")

          await this.updateMeterpointFreqConsent.mutateAsync({
            accountId,
            body: payload,
          })
          this.snackbar.addSuccess("Meter frequencies updated successfully")
        } catch (error) {
          errorReporter.report(error)
          this.snackbar.addError(
            "There was an error updating the status. Please try again.",
          )
        } finally {
          this.cancel()
        }
      },
      cancel() {
        this.resetForm()
        this.isOpen = false
      },
      resetForm() {
        this.settlementFrequency = ""
        this.meterFrequency = ""
      },
      toggleSettlementAlert() {
        this.showReadingFreqAlert = false
        this.showSettlementAlert = !this.showSettlementAlert
      },
      toggleReadFreqAlert() {
        this.showSettlementAlert = false
        this.showReadingFreqAlert = !this.showReadingFreqAlert
      },
      toggleTouAlert() {
        this.showTouAlert = !this.showTouAlert
      },
      toggleExportAlert() {
        this.showExportAlert = !this.showExportAlert
      },
      toggleExportOnlyAlert() {
        this.showExportOnlyAlert = !this.showExportOnlyAlert
      },
    },
  })
</script>
<style lang="css" scoped>
  .button-row {
    display: flex;
    justify-content: flex-end;
  }
  .mr-2 {
    margin-right: 8px;
  }
</style>
